import React, { useCallback } from 'react';
import styled from 'styled-components/macro';
import { scaleUtc } from 'd3-scale';
import { max, min } from 'd3-array';

import getPointersYCoords from './utils/getPointersYCoords';
import getYScales from './utils/getYScale';
import getPlaylistTooltipMetrics from './utils/getPlaylistTooltipMetrics';

import Chart, {
  ChartSVG,
  DefaultNoDataComponent,
} from 'components/data-visualization/Chart/Chart';
import DoubleLineChart from './DoubleLineChart';
import {
  Relative,
  StyledPerformanceTrendLoader,
} from 'components/data-containers/PerformanceTrend/shared/PerformanceTrend.style';
import Trackers from './Trackers';
import Legend from 'components/data-containers/PerformanceTrend/shared/Legend';
import Box from 'components/elements/Box';

const RelativeChart = styled(Relative)`
  height: 180px;
  width: 100%;
`;

/** To align svg with inner <g> */
const ChartPlot = styled('div')`
  ${ChartSVG} > g {
    transform: translate(0, -10px);
  }
`;

interface IDoubleChart {
  allPeriodKeys: any;
  canHaveStreams?: boolean;
  color: string;
  entityId: string;
  ghostTrackerLabels?: { streams?: string; position?: string } | null;
  fulfilled: { position: boolean; stream: boolean };
  hasNoData: { position: boolean; stream: boolean };
  isSmallScreen: boolean;
  columnOrientedChartLabel?: boolean;
  onPeriodChange: (param?: any) => any;
  onTempPeriodChange: (param?: any) => any;
  pending: { position: boolean; stream: boolean };
  positionsData: any;
  selectedEntity: any;
  selectedTempEntity: any;
  streamsData: any;
  hasBeginDottedChart?: boolean;
}

const DoubleChart: React.FC<IDoubleChart> = (props) => {
  const {
    allPeriodKeys,
    canHaveStreams = true,
    color,
    entityId,
    ghostTrackerLabels,
    fulfilled,
    hasNoData,
    isSmallScreen,
    columnOrientedChartLabel = false,
    onPeriodChange,
    onTempPeriodChange,
    pending,
    positionsData,
    selectedEntity,
    selectedTempEntity,
    streamsData,
    hasBeginDottedChart = true,
  } = props;
  const chartHasNoData = hasNoData.position && hasNoData.stream;
  const showNoDataWarning =
    (fulfilled.position &&
      fulfilled.stream &&
      !positionsData &&
      !streamsData) ||
    (hasNoData.position && hasNoData.stream);
  const chartPending = pending.position && pending.stream;
  const svgDisplayHeight = isSmallScreen ? 300 : 360;
  const svgHeight =
    !hasNoData.position && !hasNoData.stream
      ? svgDisplayHeight
      : svgDisplayHeight / 2;
  const isPositionsDisplayed =
    fulfilled.position &&
    !hasNoData.position &&
    positionsData &&
    positionsData.length > 0;
  const isStreamsDisplayed =
    fulfilled.stream &&
    !hasNoData.stream &&
    streamsData &&
    streamsData.length > 0;
  const margin = {
    left: 28,
    right: 24,
    betweenGraphs: 15,
    topHorizontalAxe: 10,
  };
  const widthLeftLabel = 40;
  const heightBottomLabel = 20;
  const svgHeightPositions = hasNoData.stream ? svgHeight : svgHeight / 2;

  const yScale = useCallback(
    getYScales({
      data: { position: positionsData, stream: streamsData },
      svgHeight,
      margin,
      heightBottomLabel,
      chartPending,
      isPositionsDisplayed,
      svgHeightPositions,
    }),
    [
      positionsData,
      streamsData,
      margin,
      heightBottomLabel,
      isPositionsDisplayed,
      svgHeightPositions,
      chartPending,
    ]
  );

  const pointersYValues = useCallback(
    getPointersYCoords({
      heightBottomLabel,
      selectedEntity,
      margin,
      yScale,
      isPositionsDisplayed,
      isStreamsDisplayed,
    }),
    [
      positionsData,
      streamsData,
      selectedEntity,
      margin,
      yScale,
      isPositionsDisplayed,
      isStreamsDisplayed,
    ]
  );
  const tempPointersYValues = useCallback(
    getPointersYCoords({
      heightBottomLabel,
      selectedEntity: selectedTempEntity,
      margin,
      yScale,
      isPositionsDisplayed,
      isStreamsDisplayed,
    }),
    [
      positionsData,
      streamsData,
      selectedTempEntity,
      margin,
      yScale,
      isPositionsDisplayed,
      isStreamsDisplayed,
    ]
  );

  return (
    <ChartPlot>
      {pending.position && (
        <RelativeChart>
          <StyledPerformanceTrendLoader />
        </RelativeChart>
      )}
      {hasNoData.position && !hasNoData.stream && (
        <RelativeChart>
          <DefaultNoDataComponent
            hasNoData={hasNoData.position}
            showNoDataWarning
          />
        </RelativeChart>
      )}
      {canHaveStreams && pending.stream && (
        <RelativeChart>
          <StyledPerformanceTrendLoader />
        </RelativeChart>
      )}
      {fulfilled.position && fulfilled.stream && (
        <Chart
          showNoDataWarning={showNoDataWarning}
          height={svgHeight + margin.betweenGraphs}
          hasNoData={chartHasNoData}
          mediaQuery="(min-width: 40em)"
          isDimmed={chartPending}
          showTooltip
          render={(renderProps): JSX.Element => {
            const xScaleTotal =
              allPeriodKeys &&
              allPeriodKeys.length > 0 &&
              scaleUtc()
                .domain([
                  Number(min(allPeriodKeys)),
                  Number(max(allPeriodKeys)),
                ])
                .range([
                  0,
                  renderProps.parentWidth -
                    margin.left -
                    margin.right -
                    widthLeftLabel,
                ]);
            const playlistMetrics = getPlaylistTooltipMetrics({
              fulfilled,
              hasNoData,
              selectedTempEntity,
              yScale,
            });
            return (
              <>
                {/** Position graph */}
                {isPositionsDisplayed && xScaleTotal && (
                  <DoubleLineChart
                    shouldTransitionIn={fulfilled.position}
                    color={color}
                    entities={positionsData}
                    entityId={entityId}
                    heightBottomLabel={heightBottomLabel}
                    label="Position"
                    margin={margin}
                    svgHeight={svgHeightPositions}
                    widthLeftLabel={widthLeftLabel}
                    yKey="position"
                    xScale={xScaleTotal}
                    yScale={yScale?.positionYScale}
                    hasBeginDottedChart={hasBeginDottedChart}
                    showHorizontalAxe
                    {...renderProps}
                  />
                )}
                {/** Stream graph */}
                {canHaveStreams && isStreamsDisplayed && xScaleTotal && (
                  <DoubleLineChart
                    shouldTransitionIn={fulfilled.stream}
                    color={color}
                    entities={streamsData}
                    entityId={entityId}
                    heightBottomLabel={heightBottomLabel}
                    label="Streams"
                    margin={margin}
                    offset={
                      hasNoData.position ? 0 : renderProps.parentHeight / 2 - 7
                    }
                    svgHeight={renderProps.parentHeight - margin.betweenGraphs}
                    widthLeftLabel={widthLeftLabel}
                    yKey="streamCount"
                    xScale={xScaleTotal}
                    yScale={yScale.streamYScale}
                    hasBeginDottedChart={hasBeginDottedChart}
                    showHorizontalAxe={!(isPositionsDisplayed && xScaleTotal)}
                    {...renderProps}
                  />
                )}

                {(isPositionsDisplayed || isStreamsDisplayed) && (
                  <Trackers
                    color={color}
                    entityId={entityId}
                    hasNoData={hasNoData}
                    labels={ghostTrackerLabels}
                    columnOrientedChartLabel={columnOrientedChartLabel}
                    margin={margin}
                    onPeriodChange={onPeriodChange}
                    onTempPeriodChange={onTempPeriodChange}
                    parentHeight={renderProps.parentHeight}
                    parentWidth={renderProps.parentWidth}
                    playlistMetrics={playlistMetrics}
                    pointersYValues={pointersYValues}
                    selectedEntity={selectedEntity}
                    selectedTempEntity={selectedTempEntity}
                    tempPointersYValues={tempPointersYValues}
                    tooltipRoot={renderProps.tooltipRoot}
                    widthLeftLabel={widthLeftLabel}
                    xScaleTotal={xScaleTotal}
                  />
                )}
              </>
            );
          }}
        />
      )}

      {canHaveStreams && hasNoData.stream && !hasNoData.position && (
        <RelativeChart>
          <DefaultNoDataComponent
            hasNoData={hasNoData.stream}
            showNoDataWarning
          />
        </RelativeChart>
      )}

      <Box mb={2} textAlign="left">
        <Legend />
      </Box>
    </ChartPlot>
  );
};

export default DoubleChart;
